import { FormattedDate, FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'

import type { ColumnDef } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import { Typography } from '@gto/ui/typography'

import { getListPartnerCouponsQueryKey, useUpdateCoupon } from '@/api/coupon-hooks'
import { CopyButton } from '@/components/copy-button'
import { RowActionsMenu } from '@/components/row-actions-menu'
import { StatusBadge } from '@/components/status-badge'
import { fixedColumn, fluidColumn, headerWithHint } from '@/components/table-columns'
import { deriveCouponStatus } from '@/domain/coupon-status'
import {
  type AffiliateAdminCouponSchema,
  couponExpiresAt,
  type CouponPlan,
  scopePlanNames,
} from '@/domain/coupons'
import { toAmount } from '@/utils/to-amount'

import { CouponTagPill } from './coupon-tag-pill'

export type CouponColumnsOptions = {
  partnerId: string
  plans: CouponPlan[]
  /** The render's single instant — see the `now` note in `CouponsTab`. */
  now: Date
  onError: (e: unknown) => void
  clearError: () => void
  onBatchFilter: (batchId: string) => void
}

// Named rather than positional: the two trailing callbacks have compatible
// shapes, so a mis-ordered positional call would type-check silently.
export const useCouponColumns = ({
  partnerId,
  plans,
  now,
  onError,
  clearError,
  onBatchFilter,
}: CouponColumnsOptions): ColumnDef<AffiliateAdminCouponSchema>[] => {
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const invalidateCoupons = () =>
    void queryClient.invalidateQueries({ queryKey: getListPartnerCouponsQueryKey(partnerId) })
  const toggleCoupon = useUpdateCoupon({
    mutation: { onMutate: clearError, onSuccess: invalidateCoupons, onError },
  })

  // Only the four columns the endpoint can order by stay sortable — sorting is ON
  // per column by default here (the inverse of DataGridPro), so every other one
  // carries `enableSorting: false`: the table would otherwise offer a header click
  // that reorders the ten rows this page happens to hold.
  return createColumns<AffiliateAdminCouponSchema>([
    {
      accessorKey: 'code',
      header: formatMessage({ id: 'admin.partnerDetail.coupons.code' }),
      ...fluidColumn({ min: 140, flex: 1 }),
      cell: ({ row }) => (
        <div className="flex flex-row items-center gap-1">
          {row.original.code}
          <CopyButton text={row.original.code} />
        </div>
      ),
    },
    {
      id: 'tags',
      header: formatMessage({ id: 'coupon.tags.column' }),
      ...fluidColumn({ min: 120, flex: 0.8 }),
      enableSorting: false,
      cell: ({ row }) =>
        row.original.tags.length === 0 ? (
          <Typography
            color="text.secondary"
            variant="body2"
          >
            —
          </Typography>
        ) : (
          <div className="flex min-w-0 flex-wrap gap-1">
            {row.original.tags.map((tag) => (
              <CouponTagPill
                key={tag.id}
                compact
                tag={tag}
              />
            ))}
          </div>
        ),
    },
    {
      accessorKey: 'discount_value',
      header: formatMessage({ id: 'admin.partnerDetail.coupons.discount' }),
      ...fluidColumn({ min: 80, flex: 0.5, align: 'right' }),
      cell: ({ row }) => (
        <Typography
          color="warning"
          variant="body2"
        >
          {String(row.original.discount_value)}% off
        </Typography>
      ),
    },
    // Per-code performance, from the aggregation the list endpoint already
    // sends. Not sortable: the endpoint's `ordering` has no key for either, so a
    // header click could only reorder the current page.
    {
      id: 'orders',
      header: headerWithHint(
        formatMessage({ id: 'admin.partnerDetail.coupons.orders' }),
        formatMessage({ id: 'admin.partnerDetail.coupons.ordersTooltip' }),
      ),
      ...fluidColumn({ min: 90, flex: 0.5, align: 'right' }),
      enableSorting: false,
      cell: ({ row }) => {
        const orders = row.original.stats.total_orders

        return (
          <Typography
            color={orders === 0 ? 'text.disabled' : undefined}
            variant="body2"
          >
            <FormattedNumber value={orders} />
          </Typography>
        )
      },
    },
    {
      id: 'revenue',
      header: headerWithHint(
        formatMessage({ id: 'admin.partnerDetail.coupons.revenue' }),
        formatMessage({ id: 'admin.partnerDetail.coupons.revenueTooltip' }),
      ),
      ...fluidColumn({ min: 100, flex: 0.6, align: 'right' }),
      enableSorting: false,
      cell: ({ row }) => {
        const amount = toAmount(
          row.original.stats.total_revenue_usd,
          'coupons table total_revenue_usd:',
        )

        return (
          <Typography
            color={amount === 0 ? 'text.disabled' : undefined}
            variant="body2"
          >
            <FormattedNumber
              currency="USD"
              maximumFractionDigits={0}
              style="currency"
              value={amount}
            />
          </Typography>
        )
      },
    },
    {
      id: 'scope',
      header: formatMessage({ id: 'admin.partnerDetail.coupons.scope' }),
      ...fluidColumn({ min: 120, flex: 0.8 }),
      enableSorting: false,
      cell: ({ row }) => {
        const planIds = row.original.plan_ids

        if (planIds.length === 0) {
          return (
            <Typography
              color="text.secondary"
              variant="body2"
            >
              <FormattedMessage id="coupon.scope.all" />
            </Typography>
          )
        }

        const names = scopePlanNames(planIds, plans)

        // Show the name only for a genuinely single-plan scope. Multiple plans
        // — or ids the catalog hasn't loaded / can't resolve — collapse to a
        // count (never a raw id, and never one name standing in for several).
        if (planIds.length === 1 && names.length === 1) {
          return names[0]
        }

        return formatMessage(
          { id: 'admin.partnerDetail.coupons.scopePlans' },
          { count: planIds.length },
        )
      },
    },
    {
      id: 'auto_cancelled',
      header: formatMessage({ id: 'coupon.autoRenewal' }),
      ...fluidColumn({ min: 120, flex: 0.7 }),
      enableSorting: false,
      cell: ({ row }) => (
        <Typography
          color="text.secondary"
          variant="body2"
        >
          <FormattedMessage
            id={row.original.auto_cancelled ? 'coupon.autoRenewalOff' : 'coupon.autoRenewalOn'}
          />
        </Typography>
      ),
    },
    // Keyed by the endpoint's derived `expires_at` (created_at + days_valid), not
    // by the stored `days_valid` it renders from, so the sort model's field maps
    // straight onto `ordering` — never-expiring codes sort last ascending.
    {
      // `accessorFn`, not `accessorKey`: `expires_at` is derived (created_at +
      // days_valid), not stored on the row. It must be an ACCESSOR column rather than a
      // display one even so — TanStack only offers a sort on a column that has an
      // accessor, and this is one of the four the endpoint can order by.
      id: 'expires_at',
      accessorFn: (row) => couponExpiresAt(row),
      header: formatMessage({ id: 'admin.partnerDetail.coupons.expiry' }),
      ...fluidColumn({ min: 100, flex: 0.6 }),
      cell: ({ getValue }) => {
        // Show the absolute expiry date (creation + days_valid), matching the
        // batch CSV's "Expires at" column rather than a raw "N days" duration.
        const expiresAt = getValue<Date | null>()

        if (expiresAt === null) {
          return (
            <Typography
              color="text.secondary"
              variant="body2"
            >
              <FormattedMessage id="admin.partnerDetail.coupons.noExpiry" />
            </Typography>
          )
        }

        return (
          <FormattedDate
            day="numeric"
            month="short"
            value={expiresAt}
            year="numeric"
          />
        )
      },
    },
    {
      accessorKey: 'created_at',
      header: formatMessage({ id: 'admin.partnerDetail.coupons.created' }),
      ...fluidColumn({ min: 110, flex: 0.6 }),
      cell: ({ row }) => (
        <Typography
          color="text.secondary"
          variant="body2"
        >
          <FormattedDate
            day="numeric"
            month="short"
            value={row.original.created_at}
            year="numeric"
          />
        </Typography>
      ),
    },
    {
      accessorKey: 'is_active',
      header: formatMessage({ id: 'coupons.status' }),
      ...fluidColumn({ min: 100, flex: 0.5 }),
      enableSorting: false,
      // The badge shows the DERIVED lifecycle status, not the raw `is_active`
      // flag this column is still keyed by — the field name only identifies the
      // column to the table.
      cell: ({ row }) => <StatusBadge status={deriveCouponStatus(row.original, now)} />,
    },
    {
      id: 'actions',
      header: '',
      ...fixedColumn({ min: 48, align: 'center' }),
      enableSorting: false,
      cell: ({ row }) => {
        // Narrowed through a local so the closure below keeps it non-null (a
        // property read would widen back to `string | null` inside the handler).
        const batchId = row.original.batch_id

        return (
          <RowActionsMenu
            actions={[
              row.original.is_active
                ? {
                    label: <FormattedMessage id="admin.partnerDetail.coupons.deactivate" />,
                    color: 'error.main',
                    disabled: toggleCoupon.isPending,
                    onClick: () =>
                      toggleCoupon.mutate({
                        partnerId,
                        couponId: row.original.id,
                        data: { is_active: false },
                      }),
                  }
                : {
                    label: <FormattedMessage id="admin.partnerDetail.coupons.activate" />,
                    disabled: toggleCoupon.isPending,
                    onClick: () =>
                      toggleCoupon.mutate({
                        partnerId,
                        couponId: row.original.id,
                        data: { is_active: true },
                      }),
                  },
              // The way into the batch scope, now that the facet cannot offer a
              // dropdown of every batch (nothing enumerates them server-side, and
              // one page can only ever list its own). From any code on screen the
              // admin can pull up the rest of the batch it was minted in.
              ...(batchId === null
                ? []
                : [
                    {
                      label: <FormattedMessage id="admin.partnerDetail.coupons.filterBatch" />,
                      dividerBefore: true,
                      onClick: () => onBatchFilter(batchId),
                    },
                  ]),
            ]}
          />
        )
      },
    },
  ])
}
